const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');

/**
 * MadBot Pose AI
 * Single Scratch 3.0 / scratch-vm extension for:
 * - body pose detection
 * - body landmarks
 * - hand detection
 * - hand landmarks
 * - finger status
 * - gesture detection
 * - body + hand combined logic
 *
 * Uses MediaPipe Tasks Vision through browser ESM dynamic import.
 */

/* CDN library and model paths */
const MEDIAPIPE_TASKS_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
const MEDIAPIPE_WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';

const LOCAL_POSE_MODEL = '/static/models/mediapipe/pose_landmarker_lite.task';
const CDN_POSE_MODEL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task';

const LOCAL_HAND_MODEL = '/static/models/mediapipe/hand_landmarker.task';
const CDN_HAND_MODEL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task';

const sectionLabel = text => ({
    blockType: BlockType.LABEL,
    text
});

class Scratch3MadBotPoseAI {
    constructor (runtime) {
        this.runtime = runtime;

        /* Model/loading state */
        this.loadingStatus = 'not loaded';
        this.errorMessage = '';
        this.vision = null;
        this.PoseLandmarker = null;
        this.HandLandmarker = null;
        this.FilesetResolver = null;
        this.poseLandmarker = null;
        this.handLandmarker = null;
        this.modelsReady = false;
        this.modelLoadPromise = null;

        /* Camera state */
        this.cameraOn = false;
        this.cameraSource = 'front';
        this.showPreview = true;
        this.cameraTransparency = 0;
        this.stream = null;
        this.video = null;

        /* AI running state */
        this.running = false;
        this.loopHandle = null;

        /* Overlay */
        this.overlayContainer = null;
        this.overlayCanvas = null;
        this.overlayCtx = null;
        this.stageHost = null;

        this.overlay = {
            bodyPoints: false,
            bodyLines: false,
            bodyLabels: false,
            handPoints: false,
            handLines: false,
            handLabels: false
        };

        this.colors = {
            bodyPoint: '#ff0000',
            bodyLine: '#00ff00',
            handPoint: '#ff0000',
            handLine: '#00ff00',
            labelText: '#ffffff',
            labelBg: 'rgba(0,0,0,0.65)'
        };

        /* Detection data */
        this.body = null;
        this.bodies = [];
        this.hands = [];

        /* Motion data */
        this.previousBodyCenter = null;
        this.previousBodySize = 0;
        this.previousTime = 0;
        this.bodyMovementDirectionValue = '';
        this.bodyMovementSpeedValue = 0;

        /* Confidence */
        this.minimumConfidence = 60;

        /* Begin downloading models as soon as the extension is added. */
        this.loadModels().catch(error => {
            // loadModels already updates the status and displays the error.
            // Catch here to prevent an unhandled promise rejection.
            console.error('MadBot Pose AI model preload failed:', error);
        });
    }

    getInfo () {
        return {
            id: 'madbotPoseAI',
            name: 'MadBot Pose AI',
            color1: '#6A20B0',
            color2: '#551A8F',
            color3: '#40136D',
            blocks: [
                sectionLabel('Setup & Camera'),
                {
                    opcode: 'turnCameraWithTransparency',
                    blockType: BlockType.COMMAND,
                    text: 'turn camera [STATE] transparency [VALUE] %',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'on'
                        },
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'showCameraPreview',
                    blockType: BlockType.COMMAND,
                    text: 'show camera preview [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'showHide',
                            defaultValue: 'show'
                        }
                    }
                },
                {
                    opcode: 'switchCamera',
                    blockType: BlockType.COMMAND,
                    text: 'switch camera to [SOURCE]',
                    arguments: {
                        SOURCE: {
                            type: ArgumentType.STRING,
                            menu: 'cameraSource',
                            defaultValue: 'front'
                        }
                    }
                },
                {
                    opcode: 'initializePoseAI',
                    blockType: BlockType.COMMAND,
                    text: 'initialize pose AI'
                },
                {
                    opcode: 'setPoseAI',
                    blockType: BlockType.COMMAND,
                    text: '[STATE] MadBot Pose AI',
                    hideFromPalette: true,
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'startStop',
                            defaultValue: 'start'
                        }
                    }
                },
                {
                    opcode: 'isPoseAIRunning',
                    blockType: BlockType.BOOLEAN,
                    text: 'is MadBot Pose AI running?'
                },
                {
                    opcode: 'modelLoadingStatus',
                    blockType: BlockType.REPORTER,
                    text: 'model loading status'
                },
                {
                    opcode: 'setMinimumConfidence',
                    blockType: BlockType.COMMAND,
                    text: 'set minimum confidence to [VALUE] %',
                    arguments: {
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 60
                        }
                    }
                },
                {
                    opcode: 'clearPoseOverlay',
                    blockType: BlockType.COMMAND,
                    text: 'clear pose overlay'
                },

                sectionLabel('Body Detection'),
                {
                    opcode: 'isBodyDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is body detected?'
                },
                {
                    opcode: 'numberOfBodiesDetected',
                    blockType: BlockType.REPORTER,
                    text: 'number of bodies detected'
                },
                {
                    opcode: 'confidenceOfBody',
                    blockType: BlockType.REPORTER,
                    text: 'confidence of body'
                },
                {
                    opcode: 'isBodyNumberDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is body number [BODY] detected?',
                    arguments: {
                        BODY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'confidenceOfBodyNumber',
                    blockType: BlockType.REPORTER,
                    text: 'confidence of body number [BODY]',
                    arguments: {
                        BODY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },

                sectionLabel('Body Points'),
                {
                    opcode: 'bodyPointValue',
                    blockType: BlockType.REPORTER,
                    text: '[POSITION] of body point [POINT]',
                    arguments: {
                        POSITION: {
                            type: ArgumentType.STRING,
                            menu: 'positionMenu',
                            defaultValue: 'x position'
                        },
                        POINT: {
                            type: ArgumentType.STRING,
                            menu: 'bodyPointMenu',
                            defaultValue: 'nose'
                        }
                    }
                },
                {
                    opcode: 'bodyPointValueOfBody',
                    blockType: BlockType.REPORTER,
                    text: '[POSITION] of body point [POINT] of body number [BODY]',
                    arguments: {
                        POSITION: {
                            type: ArgumentType.STRING,
                            menu: 'positionMenu',
                            defaultValue: 'x position'
                        },
                        POINT: {
                            type: ArgumentType.STRING,
                            menu: 'bodyPointMenu',
                            defaultValue: 'nose'
                        },
                        BODY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'setBodyOverlay',
                    blockType: BlockType.COMMAND,
                    text: 'set body points [POINTS] skeleton lines [LINES]',
                    arguments: {
                        POINTS: {
                            type: ArgumentType.STRING,
                            menu: 'showHide',
                            defaultValue: 'hide'
                        },
                        LINES: {
                            type: ArgumentType.STRING,
                            menu: 'showHide',
                            defaultValue: 'hide'
                        }
                    }
                },
                {
                    opcode: 'setBodyPointColor',
                    blockType: BlockType.COMMAND,
                    text: 'set body point color to [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR || ArgumentType.STRING,
                            defaultValue: '#ff0000'
                        }
                    }
                },
                {
                    opcode: 'setBodyLineColor',
                    blockType: BlockType.COMMAND,
                    text: 'set body line color to [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR || ArgumentType.STRING,
                            defaultValue: '#00ff00'
                        }
                    }
                },

                sectionLabel('Body Pose & Movement'),
                {
                    opcode: 'isPoseDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is [POSE] pose detected?',
                    arguments: {
                        POSE: {
                            type: ArgumentType.STRING,
                            menu: 'poseMenu',
                            defaultValue: 'both hands up'
                        }
                    }
                },
                {
                    opcode: 'isBodyMoving',
                    blockType: BlockType.BOOLEAN,
                    text: 'is body moving [DIRECTION]?',
                    arguments: {
                        DIRECTION: {
                            type: ArgumentType.STRING,
                            menu: 'movementDirectionMenu',
                            defaultValue: 'left'
                        }
                    }
                },
                {
                    opcode: 'bodyMovementDirection',
                    blockType: BlockType.REPORTER,
                    text: 'body movement direction'
                },
                {
                    opcode: 'bodyMovementSpeed',
                    blockType: BlockType.REPORTER,
                    text: 'body movement speed'
                },

                sectionLabel('Body Angles & Distance'),
                {
                    opcode: 'angleOfBodyJoint',
                    blockType: BlockType.REPORTER,
                    text: 'angle of body joint [JOINT]',
                    arguments: {
                        JOINT: {
                            type: ArgumentType.STRING,
                            menu: 'bodyJointMenu',
                            defaultValue: 'left elbow'
                        }
                    }
                },
                {
                    opcode: 'distanceBetweenBodyPoints',
                    blockType: BlockType.REPORTER,
                    text: 'distance between body point [POINT1] and [POINT2]',
                    arguments: {
                        POINT1: {
                            type: ArgumentType.STRING,
                            menu: 'bodyPointMenu',
                            defaultValue: 'left wrist'
                        },
                        POINT2: {
                            type: ArgumentType.STRING,
                            menu: 'bodyPointMenu',
                            defaultValue: 'right wrist'
                        }
                    }
                },

                sectionLabel('Hand Detection'),
                {
                    opcode: 'isHandDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is hand detected?'
                },
                {
                    opcode: 'numberOfHandsDetected',
                    blockType: BlockType.REPORTER,
                    text: 'number of hands detected'
                },
                {
                    opcode: 'isHandTypeDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is [HAND] detected?',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'handDetectMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'confidenceOfHand',
                    blockType: BlockType.REPORTER,
                    text: 'confidence of [HAND]',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'handDetectMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'isHandNumberDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is hand number [HANDNUM] detected?',
                    arguments: {
                        HANDNUM: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'handNumberIs',
                    blockType: BlockType.BOOLEAN,
                    text: 'hand number [HANDNUM] is [SIDE]',
                    arguments: {
                        HANDNUM: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        },
                        SIDE: {
                            type: ArgumentType.STRING,
                            menu: 'leftRightMenu',
                            defaultValue: 'left'
                        }
                    }
                },

                sectionLabel('Hand Points'),
                {
                    opcode: 'handPointValue',
                    blockType: BlockType.REPORTER,
                    text: '[POSITION] of [HAND] point [POINT]',
                    arguments: {
                        POSITION: {
                            type: ArgumentType.STRING,
                            menu: 'positionMenu',
                            defaultValue: 'x position'
                        },
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'handPointHandMenu',
                            defaultValue: 'left hand'
                        },
                        POINT: {
                            type: ArgumentType.STRING,
                            menu: 'handPointMenu',
                            defaultValue: 'wrist'
                        }
                    }
                },
                {
                    opcode: 'setHandOverlay',
                    blockType: BlockType.COMMAND,
                    text: 'set hand points [POINTS] hand lines [LINES]',
                    arguments: {
                        POINTS: {
                            type: ArgumentType.STRING,
                            menu: 'showHide',
                            defaultValue: 'hide'
                        },
                        LINES: {
                            type: ArgumentType.STRING,
                            menu: 'showHide',
                            defaultValue: 'hide'
                        }
                    }
                },
                {
                    opcode: 'setHandPointColor',
                    blockType: BlockType.COMMAND,
                    text: 'set hand point color to [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR || ArgumentType.STRING,
                            defaultValue: '#ff0000'
                        }
                    }
                },
                {
                    opcode: 'setHandLineColor',
                    blockType: BlockType.COMMAND,
                    text: 'set hand line color to [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR || ArgumentType.STRING,
                            defaultValue: '#00ff00'
                        }
                    }
                },

                sectionLabel('Finger Status'),
                {
                    opcode: 'isFingerUp',
                    blockType: BlockType.BOOLEAN,
                    text: 'is [FINGER] finger up on [HAND]?',
                    arguments: {
                        FINGER: {
                            type: ArgumentType.STRING,
                            menu: 'fingerMenu',
                            defaultValue: 'index finger'
                        },
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'numberOfFingersUp',
                    blockType: BlockType.REPORTER,
                    text: 'number of fingers up on [HAND]',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'isFistMade',
                    blockType: BlockType.BOOLEAN,
                    text: 'is fist made on [HAND]?',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'isPalmOpen',
                    blockType: BlockType.BOOLEAN,
                    text: 'is palm open on [HAND]?',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'isPointingWithFinger',
                    blockType: BlockType.BOOLEAN,
                    text: 'is pointing with [FINGER] on [HAND]?',
                    arguments: {
                        FINGER: {
                            type: ArgumentType.STRING,
                            menu: 'fingerMenu',
                            defaultValue: 'index finger'
                        },
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },

                sectionLabel('Hand Gestures'),
                {
                    opcode: 'isHandGestureDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is hand gesture [GESTURE] detected?',
                    arguments: {
                        GESTURE: {
                            type: ArgumentType.STRING,
                            menu: 'gestureMenu',
                            defaultValue: 'open palm'
                        }
                    }
                },
                {
                    opcode: 'isHandGestureOnHandDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is [HAND] gesture [GESTURE] detected?',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        },
                        GESTURE: {
                            type: ArgumentType.STRING,
                            menu: 'gestureMenu',
                            defaultValue: 'open palm'
                        }
                    }
                },
                {
                    opcode: 'recognizedHandGesture',
                    blockType: BlockType.REPORTER,
                    text: 'recognized hand gesture on [HAND]',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },

                sectionLabel('Hand Distance & Angle'),
                {
                    opcode: 'distanceBetweenHandPoints',
                    blockType: BlockType.REPORTER,
                    text: 'distance between [HAND] point [POINT1] and point [POINT2]',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'handPointHandMenu',
                            defaultValue: 'left hand'
                        },
                        POINT1: {
                            type: ArgumentType.STRING,
                            menu: 'handPointMenu',
                            defaultValue: 'thumb tip'
                        },
                        POINT2: {
                            type: ArgumentType.STRING,
                            menu: 'handPointMenu',
                            defaultValue: 'index finger tip'
                        }
                    }
                },
                {
                    opcode: 'angleOfHandFinger',
                    blockType: BlockType.REPORTER,
                    text: 'angle of [HAND] finger [FINGER]',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'handPointHandMenu',
                            defaultValue: 'left hand'
                        },
                        FINGER: {
                            type: ArgumentType.STRING,
                            menu: 'fingerMenu',
                            defaultValue: 'index finger'
                        }
                    }
                },

                sectionLabel('Body + Hand Combined'),
                {
                    opcode: 'isHandNearBodyArea',
                    blockType: BlockType.BOOLEAN,
                    text: 'is [HAND] near [AREA]?',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        },
                        AREA: {
                            type: ArgumentType.STRING,
                            menu: 'bodyAreaMenu',
                            defaultValue: 'face'
                        }
                    }
                },
                {
                    opcode: 'isHandAboveBodyArea',
                    blockType: BlockType.BOOLEAN,
                    text: 'is [HAND] above [AREA]?',
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        },
                        AREA: {
                            type: ArgumentType.STRING,
                            menu: 'bodyAreaMenu',
                            defaultValue: 'head'
                        }
                    }
                },
                {
                    opcode: 'areBothHandsState',
                    blockType: BlockType.BOOLEAN,
                    text: 'are both hands [STATE]?',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'bothHandsStateMenu',
                            defaultValue: 'joined'
                        }
                    }
                },

                sectionLabel('Body + Hand Events'),
                {
                    opcode: 'whenBodyDetected',
                    blockType: BlockType.HAT,
                    text: 'when body detected',
                    isEdgeActivated: true
                },
                {
                    opcode: 'whenBodyLost',
                    blockType: BlockType.HAT,
                    text: 'when body lost',
                    isEdgeActivated: true
                },
                {
                    opcode: 'whenHandDetected',
                    blockType: BlockType.HAT,
                    text: 'when [HAND] detected',
                    isEdgeActivated: true,
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'whenHandLost',
                    blockType: BlockType.HAT,
                    text: 'when [HAND] lost',
                    isEdgeActivated: true,
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        }
                    }
                },
                {
                    opcode: 'whenPoseDetected',
                    blockType: BlockType.HAT,
                    text: 'when pose [POSE] detected',
                    isEdgeActivated: true,
                    arguments: {
                        POSE: {
                            type: ArgumentType.STRING,
                            menu: 'poseMenu',
                            defaultValue: 'both hands up'
                        }
                    }
                },
                {
                    opcode: 'whenHandGestureDetected',
                    blockType: BlockType.HAT,
                    text: 'when hand gesture [GESTURE] detected',
                    isEdgeActivated: true,
                    arguments: {
                        GESTURE: {
                            type: ArgumentType.STRING,
                            menu: 'gestureMenu',
                            defaultValue: 'open palm'
                        }
                    }
                },
                {
                    opcode: 'whenHandGestureOnHandDetected',
                    blockType: BlockType.HAT,
                    text: 'when [HAND] gesture [GESTURE] detected',
                    isEdgeActivated: true,
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        },
                        GESTURE: {
                            type: ArgumentType.STRING,
                            menu: 'gestureMenu',
                            defaultValue: 'open palm'
                        }
                    }
                },
                {
                    opcode: 'whenBodyMoves',
                    blockType: BlockType.HAT,
                    text: 'when body moves [DIRECTION]',
                    isEdgeActivated: true,
                    arguments: {
                        DIRECTION: {
                            type: ArgumentType.STRING,
                            menu: 'movementDirectionMenu',
                            defaultValue: 'left'
                        }
                    }
                },
                {
                    opcode: 'whenNumberOfFingersIs',
                    blockType: BlockType.HAT,
                    text: 'when number of fingers on [HAND] is [NUM]',
                    isEdgeActivated: true,
                    arguments: {
                        HAND: {
                            type: ArgumentType.STRING,
                            menu: 'fingerHandMenu',
                            defaultValue: 'any hand'
                        },
                        NUM: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 5
                        }
                    }
                }
            ],
            menus: {
                onOff: {
                    acceptReporters: true,
                    items: ['on', 'off']
                },
                startStop: {
                    acceptReporters: true,
                    items: ['start', 'stop']
                },
                showHide: {
                    acceptReporters: true,
                    items: ['show', 'hide']
                },
                cameraSource: {
                    acceptReporters: true,
                    items: ['front', 'back']
                },
                positionMenu: {
                    acceptReporters: true,
                    items: ['x position', 'y position', 'z position', 'visibility']
                },
                bodyPointMenu: {
                    acceptReporters: true,
                    items: [
                        'nose',
                        'left eye',
                        'right eye',
                        'left ear',
                        'right ear',
                        'left shoulder',
                        'right shoulder',
                        'left elbow',
                        'right elbow',
                        'left wrist',
                        'right wrist',
                        'left hip',
                        'right hip',
                        'left knee',
                        'right knee',
                        'left ankle',
                        'right ankle',
                        'left heel',
                        'right heel',
                        'left foot',
                        'right foot'
                    ]
                },
                poseMenu: {
                    acceptReporters: true,
                    items: [
                        'left hand up',
                        'right hand up',
                        'both hands up',
                        'left hand down',
                        'right hand down',
                        'standing',
                        'sitting',
                        'jumping',
                        'leaning left',
                        'leaning right',
                        'arms open',
                        'T-pose',
                        'hands near face',
                        'hands joined'
                    ]
                },
                movementDirectionMenu: {
                    acceptReporters: true,
                    items: ['left', 'right', 'up', 'down', 'near', 'far']
                },
                bodyJointMenu: {
                    acceptReporters: true,
                    items: [
                        'left elbow',
                        'right elbow',
                        'left knee',
                        'right knee',
                        'left shoulder',
                        'right shoulder',
                        'left hip',
                        'right hip'
                    ]
                },
                handDetectMenu: {
                    acceptReporters: true,
                    items: ['left hand', 'right hand', 'any hand', 'both hands']
                },
                handPointHandMenu: {
                    acceptReporters: true,
                    items: ['left hand', 'right hand', 'hand number 1', 'hand number 2']
                },
                fingerHandMenu: {
                    acceptReporters: true,
                    items: ['left hand', 'right hand', 'any hand']
                },
                leftRightMenu: {
                    acceptReporters: true,
                    items: ['left', 'right']
                },
                handPointMenu: {
                    acceptReporters: true,
                    items: [
                        'wrist',
                        'thumb base',
                        'thumb lower joint',
                        'thumb upper joint',
                        'thumb tip',
                        'index finger base',
                        'index finger lower joint',
                        'index finger middle joint',
                        'index finger tip',
                        'middle finger base',
                        'middle finger lower joint',
                        'middle finger middle joint',
                        'middle finger tip',
                        'ring finger base',
                        'ring finger lower joint',
                        'ring finger middle joint',
                        'ring finger tip',
                        'pinky finger base',
                        'pinky finger lower joint',
                        'pinky finger middle joint',
                        'pinky finger tip'
                    ]
                },
                fingerMenu: {
                    acceptReporters: true,
                    items: ['thumb', 'index finger', 'middle finger', 'ring finger', 'pinky finger']
                },
                gestureMenu: {
                    acceptReporters: true,
                    items: [
                        'open palm',
                        'fist',
                        'thumbs up',
                        'thumbs down',
                        'victory sign',
                        'peace sign',
                        'pointing up',
                        'pointing left',
                        'pointing right',
                        'OK sign',
                        'rock sign',
                        'call me sign',
                        'one finger',
                        'two fingers',
                        'three fingers',
                        'four fingers',
                        'five fingers'
                    ]
                },
                bodyAreaMenu: {
                    acceptReporters: true,
                    items: ['face', 'head', 'chest', 'left shoulder', 'right shoulder', 'waist']
                },
                bothHandsStateMenu: {
                    acceptReporters: true,
                    items: ['joined', 'apart', 'crossed', 'raised', 'down']
                }
            }
        };
    }

    /* --------------------------------------------------
     * Setup & camera
     * -------------------------------------------------- */

    async turnCamera (args) {
        if (args.STATE === 'on') {
            await this.startCamera();
        } else {
            this.stopCamera();
        }
    }

    async initializePoseAI () {
        await this.startPoseAI();
    }

    async setPoseAI (args) {
        if (args.STATE === 'start') {
            await this.startPoseAI();
        } else {
            this.stopPoseAI();
        }
    }

    async turnCameraWithTransparency (args) {
        this.cameraTransparency = this.clamp(Cast.toNumber(args.VALUE), 0, 100);
        this.applyScratchStagePreview();

        if (args.STATE === 'on') {
            await this.startCamera();
        } else {
            this.stopCamera();
        }
    }

    showCameraPreview (args) {
        this.setPreview(args.STATE === 'show');
    }

    setCameraPreview (args) {
        this.setPreview(args.STATE === 'show');
    }

    setCameraTransparency (args) {
        this.cameraTransparency = this.clamp(Cast.toNumber(args.VALUE), 0, 100);
        this.applyScratchStagePreview();
    }

    async switchCamera (args) {
        const source = String(args.SOURCE || 'front').toLowerCase();

        this.cameraSource = source === 'back' || source === 'environment' ? 'back' : 'front';

        if (this.cameraOn) {
            await this.restartCameraForSourceChange();
        }
    }

    async startPoseAI () {
        await this.loadModels();
        if (!this.cameraOn) {
            await this.startCamera();
        }
        this.running = true;
        this.startDetectionLoop();
    }

    stopPoseAI () {
        this.running = false;
        if (this.loopHandle) {
            cancelAnimationFrame(this.loopHandle);
            this.loopHandle = null;
        }
    }

    isPoseAIRunning () {
        return this.running;
    }

    modelLoadingStatus () {
        return this.loadingStatus || '';
    }

    async loadModels () {
        if (this.modelsReady) return;

        /* Reuse the active download instead of starting another one. */
        if (this.modelLoadPromise) {
            await this.modelLoadPromise;
            return;
        }

        this.modelLoadPromise = this.performModelLoad();

        try {
            await this.modelLoadPromise;
        } finally {
            /* Allow another attempt if loading failed. */
            if (!this.modelsReady) {
                this.modelLoadPromise = null;
            }
        }
    }

    async performModelLoad () {
        try {
            this.loadingStatus = 'loading';
            this.showLoadingPopup('Loading MadBot Pose AI models...');

            const dynamicImport = new Function('url', 'return import(url);');
            const tasksVision = await dynamicImport(MEDIAPIPE_TASKS_URL);

            this.FilesetResolver = tasksVision.FilesetResolver;
            this.PoseLandmarker = tasksVision.PoseLandmarker;
            this.HandLandmarker = tasksVision.HandLandmarker;

            this.vision = await this.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);

            await this.createPoseModel();
            await this.createHandModel();

            this.modelsReady = true;
            this.loadingStatus = 'ready';
            this.showLoadingPopup('MadBot Pose AI is ready.');
            setTimeout(() => this.hideLoadingPopup(), 500);
        } catch (error) {
            this.loadingStatus = 'error';
            this.errorMessage = error && error.message ? error.message : String(error);
            this.showLoadingError(this.errorMessage);
            throw error;
        }
    }

    async createPoseModel () {
        const options = modelPath => ({
            baseOptions: {
                modelAssetPath: modelPath,
                delegate: 'GPU'
            },
            runningMode: 'VIDEO',
            numPoses: 1,
            minPoseDetectionConfidence: this.minimumConfidence / 100,
            minPosePresenceConfidence: this.minimumConfidence / 100,
            minTrackingConfidence: this.minimumConfidence / 100
        });

        try {
            this.poseLandmarker = await this.PoseLandmarker.createFromOptions(this.vision, options(LOCAL_POSE_MODEL));
        } catch (e) {
            this.poseLandmarker = await this.PoseLandmarker.createFromOptions(this.vision, options(CDN_POSE_MODEL));
        }
    }

    async createHandModel () {
        const options = modelPath => ({
            baseOptions: {
                modelAssetPath: modelPath,
                delegate: 'GPU'
            },
            runningMode: 'VIDEO',
            numHands: 2,
            minHandDetectionConfidence: this.minimumConfidence / 100,
            minHandPresenceConfidence: this.minimumConfidence / 100,
            minTrackingConfidence: this.minimumConfidence / 100
        });

        try {
            this.handLandmarker = await this.HandLandmarker.createFromOptions(this.vision, options(LOCAL_HAND_MODEL));
        } catch (e) {
            this.handLandmarker = await this.HandLandmarker.createFromOptions(this.vision, options(CDN_HAND_MODEL));
        }
    }

    async startCamera () {
        try {
            this.createHiddenVideo();
            this.createOverlay();

            this.stopCameraStreamOnly();

            await new Promise(resolve => setTimeout(resolve, 250));

            const stream = await this.openSelectedCameraStream();

            this.stream = stream;
            this.video.srcObject = stream;

            this.video.autoplay = true;
            this.video.muted = true;
            this.video.playsInline = true;
            this.video.setAttribute('playsinline', 'true');
            this.video.setAttribute('webkit-playsinline', 'true');

            await this.video.play();

            this.cameraOn = true;
            this.applyScratchStagePreview();
        } catch (error) {
            this.cameraOn = false;
            this.loadingStatus = 'error';
            this.errorMessage = error && error.message ? error.message : String(error);
            this.showLoadingError(`Camera error: ${this.errorMessage}`);
        }
    }

    stopCamera () {
        this.cameraOn = false;
        this.stopPoseAI();
        this.disableScratchStagePreview();

        this.stopCameraStreamOnly();

        this.body = null;
        this.bodies = [];
        this.hands = [];
        this.clearPoseOverlay();
    }

    createHiddenVideo () {
        if (this.video) return;

        this.video = document.createElement('video');
        this.video.autoplay = true;
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', 'true');
        this.video.setAttribute('webkit-playsinline', 'true');

        Object.assign(this.video.style, {
            position: 'fixed',
            left: '-10000px',
            top: '-10000px',
            width: '1px',
            height: '1px',
            opacity: '0',
            pointerEvents: 'none'
        });

        document.body.appendChild(this.video);
    }

    async restartCameraForSourceChange () {
        const wasRunning = this.running;

        this.stopCameraStreamOnly();

        await new Promise(resolve => setTimeout(resolve, 300));

        await this.startCamera();

        if (wasRunning) {
            this.running = true;
            if (!this.loopHandle) {
                this.startDetectionLoop();
            }
        }
    }

    stopCameraStreamOnly () {
        if (this.stream) {
            this.stream.getTracks().forEach(track => {
                try {
                    track.stop();
                } catch (e) {
                    // Ignore camera stop errors.
                }
            });
            this.stream = null;
        }

        if (this.video) {
            try {
                this.video.pause();
                this.video.srcObject = null;
                this.video.load();
            } catch (e) {
                // Ignore video reset errors.
            }
        }

        this.cameraOn = false;
    }

    async openSelectedCameraStream () {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('Camera is not supported in this browser.');
        }

        const facingMode = this.cameraSource === 'back' ? 'environment' : 'user';

        const baseVideoSettings = {
            width: {ideal: 640},
            height: {ideal: 480}
        };

        const deviceId = await this.findCameraDeviceId(this.cameraSource);

        if (deviceId) {
            try {
                return await navigator.mediaDevices.getUserMedia({
                    video: {
                        ...baseVideoSettings,
                        deviceId: {exact: deviceId}
                    },
                    audio: false
                });
            } catch (error) {
                console.warn('MadBot Pose AI: deviceId camera failed, trying facingMode.', error);
            }
        }

        try {
            return await navigator.mediaDevices.getUserMedia({
                video: {
                    ...baseVideoSettings,
                    facingMode: {exact: facingMode}
                },
                audio: false
            });
        } catch (error) {
            console.warn('MadBot Pose AI: exact facingMode failed, trying ideal facingMode.', error);
        }

        return await navigator.mediaDevices.getUserMedia({
            video: {
                ...baseVideoSettings,
                facingMode: {ideal: facingMode}
            },
            audio: false
        });
    }

    async findCameraDeviceId (source) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            return null;
        }

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');

            if (videoDevices.length < 1) return null;

            const backKeywords = ['back', 'rear', 'environment', 'world'];
            const frontKeywords = ['front', 'user', 'face', 'selfie'];

            const keywords = source === 'back' ? backKeywords : frontKeywords;

            const matchedDevice = videoDevices.find(device => {
                const label = String(device.label || '').toLowerCase();
                return keywords.some(keyword => label.includes(keyword));
            });

            if (matchedDevice) {
                return matchedDevice.deviceId;
            }

            if (source === 'back' && videoDevices.length > 1) {
                return videoDevices[videoDevices.length - 1].deviceId;
            }

            if (source === 'front') {
                return videoDevices[0].deviceId;
            }

            return null;
        } catch (error) {
            console.warn('MadBot Pose AI: enumerateDevices failed.', error);
            return null;
        }
    }

    setPreview (visible) {
        this.showPreview = visible;
        if (this.overlayContainer) {
            this.overlayContainer.style.display = visible ? 'block' : 'none';
        }
        if (visible) {
            this.applyScratchStagePreview();
        } else {
            this.disableScratchStagePreview();
        }
    }

    applyScratchStagePreview () {
        const videoDevice = this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video;

        if (!videoDevice) return;

        if (videoDevice.setPreviewGhost) {
            videoDevice.setPreviewGhost(this.cameraTransparency);
        }

        if (this.showPreview && this.cameraOn && videoDevice.enableVideo) {
            const result = videoDevice.enableVideo();
            if (result && result.catch) {
                result.catch(error => {
                    console.warn('MadBot Pose AI: Scratch video preview error:', error);
                });
            }
        }
    }

    disableScratchStagePreview () {
        const videoDevice = this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video;

        if (videoDevice && videoDevice.disableVideo) {
            videoDevice.disableVideo();
        }
    }

    /* --------------------------------------------------
     * Detection loop
     * -------------------------------------------------- */

    startDetectionLoop () {
        if (!this.running) return;

        const loop = () => {
            if (!this.running) return;

            this.detectOnce();
            this.loopHandle = requestAnimationFrame(loop);
        };

        if (!this.loopHandle) {
            this.loopHandle = requestAnimationFrame(loop);
        }
    }

    detectOnce () {
        if (!this.modelsReady || !this.cameraOn || !this.video || this.video.readyState < 2) return;

        const now = performance.now();

        try {
            const poseResult = this.poseLandmarker ?
                this.poseLandmarker.detectForVideo(this.video, now) :
                null;

            const handResult = this.handLandmarker ?
                this.handLandmarker.detectForVideo(this.video, now) :
                null;

            this.updateBodyData(poseResult);
            this.updateHandData(handResult);
            this.updateBodyMovement(now);
            this.drawOverlay();
        } catch (error) {
            console.warn('MadBot Pose AI detection error:', error);
        }
    }

    updateBodyData (poseResult) {
        this.body = null;
        this.bodies = [];

        if (!poseResult || !poseResult.landmarks || poseResult.landmarks.length < 1) return;

        const landmarks = poseResult.landmarks[0] || [];
        const worldLandmarks = poseResult.worldLandmarks && poseResult.worldLandmarks[0] ?
            poseResult.worldLandmarks[0] :
            [];

        const confidence = this.averageVisibility(landmarks);
        const bounds = this.getBoundsFromLandmarks(landmarks);

        this.body = {
            landmarks,
            worldLandmarks,
            confidence,
            bounds
        };

        this.bodies = [this.body];
    }

    updateHandData (handResult) {
        this.hands = [];

        if (!handResult || !handResult.landmarks || handResult.landmarks.length < 1) return;

        handResult.landmarks.forEach((landmarks, index) => {
            const handednessData = handResult.handednesses &&
                handResult.handednesses[index] &&
                handResult.handednesses[index][0] ?
                handResult.handednesses[index][0] :
                null;

            const side = handednessData && handednessData.categoryName ?
                handednessData.categoryName.toLowerCase() :
                `hand number ${index + 1}`;

            const confidence = handednessData && handednessData.score ?
                Math.round(handednessData.score * 100) :
                0;

            this.hands.push({
                index,
                side,
                landmarks,
                confidence
            });
        });
    }

    updateBodyMovement (now) {
        if (!this.body || !this.body.bounds) {
            this.bodyMovementDirectionValue = '';
            this.bodyMovementSpeedValue = 0;
            this.previousBodyCenter = null;
            return;
        }

        const center = {
            x: this.body.bounds.centerX,
            y: this.body.bounds.centerY
        };

        const size = this.body.bounds.width * this.body.bounds.height;

        if (!this.previousBodyCenter) {
            this.previousBodyCenter = center;
            this.previousBodySize = size;
            this.previousTime = now;
            this.bodyMovementDirectionValue = '';
            this.bodyMovementSpeedValue = 0;
            return;
        }

        const dt = Math.max(1, now - this.previousTime);
        const dx = center.x - this.previousBodyCenter.x;
        const dy = center.y - this.previousBodyCenter.y;
        const ds = size - this.previousBodySize;

        const distance = Math.sqrt((dx * dx) + (dy * dy));
        this.bodyMovementSpeedValue = Math.round((distance / dt) * 1000);

        const threshold = 0.015;
        const sizeThreshold = 0.015;

        if (Math.abs(ds) > sizeThreshold) {
            this.bodyMovementDirectionValue = ds > 0 ? 'near' : 'far';
        } else if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > threshold) {
            this.bodyMovementDirectionValue = dx > 0 ? 'right' : 'left';
        } else if (Math.abs(dy) > threshold) {
            this.bodyMovementDirectionValue = dy < 0 ? 'up' : 'down';
        } else {
            this.bodyMovementDirectionValue = '';
        }

        this.previousBodyCenter = center;
        this.previousBodySize = size;
        this.previousTime = now;
    }

    /* --------------------------------------------------
     * Body detection reporters
     * -------------------------------------------------- */

    isBodyDetected () {
        return !!this.body;
    }

    numberOfBodiesDetected () {
        return this.bodies.length;
    }

    confidenceOfBody () {
        return this.body ? this.body.confidence : 0;
    }

    isBodyNumberDetected (args) {
        return !!this.getBody(Cast.toNumber(args.BODY));
    }

    confidenceOfBodyNumber (args) {
        const body = this.getBody(Cast.toNumber(args.BODY));
        return body ? body.confidence : 0;
    }

    getBody (bodyNumber) {
        const index = Math.max(0, Math.round(bodyNumber) - 1);
        return this.bodies[index] || null;
    }

    /* --------------------------------------------------
     * Body point reporters
     * -------------------------------------------------- */

    bodyPointValue (args) {
        return this.getBodyPointValue(1, args.POINT, args.POSITION);
    }

    bodyPointValueOfBody (args) {
        return this.getBodyPointValue(args.BODY, args.POINT, args.POSITION);
    }

    getBodyPointValue (bodyNumber, pointName, position) {
        const body = this.getBody(Cast.toNumber(bodyNumber));
        if (!body) return 0;

        const landmark = body.landmarks[this.bodyPointIndex(pointName)];
        if (!landmark) return 0;

        return this.landmarkValue(landmark, position);
    }

    bodyCenterX () {
        return this.getBodyBoundsValue(1, 'centerXStage');
    }

    bodyCenterY () {
        return this.getBodyBoundsValue(1, 'centerYStage');
    }

    bodyWidth () {
        return this.getBodyBoundsValue(1, 'widthStage');
    }

    bodyHeight () {
        return this.getBodyBoundsValue(1, 'heightStage');
    }

    bodyTopY () {
        return this.getBodyBoundsValue(1, 'topYStage');
    }

    bodyBottomY () {
        return this.getBodyBoundsValue(1, 'bottomYStage');
    }

    bodyNumberCenterX (args) {
        return this.getBodyBoundsValue(args.BODY, 'centerXStage');
    }

    bodyNumberCenterY (args) {
        return this.getBodyBoundsValue(args.BODY, 'centerYStage');
    }

    bodyNumberWidth (args) {
        return this.getBodyBoundsValue(args.BODY, 'widthStage');
    }

    bodyNumberHeight (args) {
        return this.getBodyBoundsValue(args.BODY, 'heightStage');
    }

    getBodyBoundsValue (bodyNumber, key) {
        const body = this.getBody(Cast.toNumber(bodyNumber));
        if (!body || !body.bounds) return 0;
        return Math.round(body.bounds[key] || 0);
    }

    /* --------------------------------------------------
     * Body pose and movement
     * -------------------------------------------------- */

    isPoseDetected (args) {
        return this.detectPose(args.POSE);
    }

    isBodyMoving (args) {
        return this.bodyMovementDirectionValue === args.DIRECTION;
    }

    bodyMovementDirection () {
        return this.bodyMovementDirectionValue || '';
    }

    bodyMovementSpeed () {
        return this.bodyMovementSpeedValue || 0;
    }

    detectPose (pose) {
        if (!this.body) return false;

        const p = name => this.getBodyLandmark(name);
        const leftWrist = p('left wrist');
        const rightWrist = p('right wrist');
        const leftShoulder = p('left shoulder');
        const rightShoulder = p('right shoulder');
        const leftHip = p('left hip');
        const rightHip = p('right hip');
        const leftKnee = p('left knee');
        const rightKnee = p('right knee');
        const nose = p('nose');

        if (!leftWrist || !rightWrist || !leftShoulder || !rightShoulder) return false;

        const shoulderCenterX = (leftShoulder.x + rightShoulder.x) / 2;
        const hipCenterX = leftHip && rightHip ? (leftHip.x + rightHip.x) / 2 : shoulderCenterX;

        const leftHandUp = leftWrist.y < leftShoulder.y;
        const rightHandUp = rightWrist.y < rightShoulder.y;
        const leftHandDown = leftWrist.y > leftShoulder.y;
        const rightHandDown = rightWrist.y > rightShoulder.y;

        switch (pose) {
        case 'left hand up':
            return leftHandUp;
        case 'right hand up':
            return rightHandUp;
        case 'both hands up':
            return leftHandUp && rightHandUp;
        case 'left hand down':
            return leftHandDown;
        case 'right hand down':
            return rightHandDown;
        case 'standing':
            return leftHip && rightHip && leftKnee && rightKnee &&
                leftHip.y < leftKnee.y && rightHip.y < rightKnee.y;
        case 'sitting':
            return leftHip && rightHip && leftKnee && rightKnee &&
                Math.abs(leftHip.y - leftKnee.y) < 0.18 &&
                Math.abs(rightHip.y - rightKnee.y) < 0.18;
        case 'jumping':
            return this.bodyMovementDirectionValue === 'up' && this.bodyMovementSpeedValue > 35;
        case 'leaning left':
            return shoulderCenterX < hipCenterX - 0.04;
        case 'leaning right':
            return shoulderCenterX > hipCenterX + 0.04;
        case 'arms open':
            return Math.abs(leftWrist.x - rightWrist.x) > Math.abs(leftShoulder.x - rightShoulder.x) * 1.8;
        case 'T-pose':
            return Math.abs(leftWrist.y - leftShoulder.y) < 0.08 &&
                Math.abs(rightWrist.y - rightShoulder.y) < 0.08 &&
                Math.abs(leftWrist.x - rightWrist.x) > Math.abs(leftShoulder.x - rightShoulder.x) * 1.8;
        case 'hands near face':
            return nose &&
                this.normalizedDistance(leftWrist, nose) < 0.18 &&
                this.normalizedDistance(rightWrist, nose) < 0.18;
        case 'hands joined':
            return this.normalizedDistance(leftWrist, rightWrist) < 0.12;
        default:
            return false;
        }
    }

    /* --------------------------------------------------
     * Body angles and distances
     * -------------------------------------------------- */

    angleOfBodyJoint (args) {
        const joint = args.JOINT;
        const triple = this.bodyJointPoints(joint);
        if (!triple) return 0;

        const a = this.getBodyLandmark(triple[0]);
        const b = this.getBodyLandmark(triple[1]);
        const c = this.getBodyLandmark(triple[2]);

        return this.angleBetween(a, b, c);
    }

    distanceBetweenBodyPoints (args) {
        const p1 = this.getBodyLandmark(args.POINT1);
        const p2 = this.getBodyLandmark(args.POINT2);
        return this.stageDistance(p1, p2);
    }

    /* --------------------------------------------------
     * Hand detection
     * -------------------------------------------------- */

    isHandDetected () {
        return this.hands.length > 0;
    }

    numberOfHandsDetected () {
        return this.hands.length;
    }

    isHandTypeDetected (args) {
        const hand = args.HAND;
        if (hand === 'any hand') return this.hands.length > 0;
        if (hand === 'both hands') return this.getHand('left hand') && this.getHand('right hand');
        return !!this.getHand(hand);
    }

    confidenceOfHand (args) {
        const hand = this.getHand(args.HAND);
        return hand ? hand.confidence : 0;
    }

    isHandNumberDetected (args) {
        return !!this.getHand(`hand number ${Cast.toNumber(args.HANDNUM)}`);
    }

    handNumberIs (args) {
        const hand = this.getHand(`hand number ${Cast.toNumber(args.HANDNUM)}`);
        if (!hand) return false;
        return hand.side === args.SIDE;
    }

    /* --------------------------------------------------
     * Hand points
     * -------------------------------------------------- */

    handPointValue (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return 0;

        const landmark = hand.landmarks[this.handPointIndex(args.POINT)];
        if (!landmark) return 0;

        return this.landmarkValue(landmark, args.POSITION);
    }

    /* --------------------------------------------------
     * Finger status and gestures
     * -------------------------------------------------- */

    isFingerUp (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return false;
        return this.fingerIsUp(hand, args.FINGER);
    }

    numberOfFingersUp (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return 0;
        return this.countFingersUp(hand);
    }

    isFistMade (args) {
        const hand = this.getHand(args.HAND);
        return hand ? this.countFingersUp(hand) === 0 : false;
    }

    isPalmOpen (args) {
        const hand = this.getHand(args.HAND);
        return hand ? this.countFingersUp(hand) >= 5 : false;
    }

    isPointingWithFinger (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return false;

        const finger = args.FINGER;
        const fingers = this.fingersState(hand);
        return Object.keys(fingers).every(key => {
            if (key === finger) return fingers[key];
            return !fingers[key];
        });
    }

    isHandGestureDetected (args) {
        return this.hands.some(hand => this.detectGesture(hand, args.GESTURE));
    }

    isHandGestureOnHandDetected (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return false;
        return this.detectGesture(hand, args.GESTURE);
    }

    recognizedHandGesture (args) {
        const hands = this.getHandsForSelection(args.HAND);

        if (!hands || hands.length < 1) return '';

        const gestures = [
            'open palm',
            'fist',
            'thumbs up',
            'thumbs down',
            'victory sign',
            'peace sign',
            'pointing up',
            'pointing left',
            'pointing right',
            'OK sign',
            'rock sign',
            'call me sign',
            'five fingers',
            'four fingers',
            'three fingers',
            'two fingers',
            'one finger'
        ];

        for (let i = 0; i < hands.length; i++) {
            for (let j = 0; j < gestures.length; j++) {
                if (this.detectGesture(hands[i], gestures[j])) {
                    return gestures[j];
                }
            }
        }

        return '';
    }

    detectGesture (hand, gesture) {
        if (!hand) return false;

        const fingers = this.fingersState(hand);
        const count = this.countFingersUp(hand);

        const thumb = fingers.thumb;
        const index = fingers['index finger'];
        const middle = fingers['middle finger'];
        const ring = fingers['ring finger'];
        const pinky = fingers['pinky finger'];

        const wrist = hand.landmarks[0];
        const thumbTip = hand.landmarks[4];
        const indexTip = hand.landmarks[8];

        switch (gesture) {
        case 'open palm':
            return count >= 5;
        case 'fist':
            return count === 0;
        case 'thumbs up':
            return thumb && !index && !middle && !ring && !pinky && thumbTip.y < wrist.y;
        case 'thumbs down':
            return thumb && !index && !middle && !ring && !pinky && thumbTip.y > wrist.y;
        case 'victory sign':
        case 'peace sign':
            return index && middle && !ring && !pinky;
        case 'pointing up':
            return index && !middle && !ring && !pinky && indexTip.y < wrist.y;
        case 'pointing left':
            return index && !middle && !ring && !pinky && this.stageX(indexTip.x) < this.stageX(wrist.x) - 40;
        case 'pointing right':
            return index && !middle && !ring && !pinky && this.stageX(indexTip.x) > this.stageX(wrist.x) + 40;
        case 'OK sign':
            return this.normalizedDistance(thumbTip, indexTip) < 0.06 && middle && ring && pinky;
        case 'rock sign':
            return index && pinky && !middle && !ring;
        case 'call me sign':
            return thumb && pinky && !index && !middle && !ring;
        case 'one finger':
            return count === 1;
        case 'two fingers':
            return count === 2;
        case 'three fingers':
            return count === 3;
        case 'four fingers':
            return count === 4;
        case 'five fingers':
            return count === 5;
        default:
            return false;
        }
    }

    distanceBetweenHandPoints (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return 0;

        const p1 = hand.landmarks[this.handPointIndex(args.POINT1)];
        const p2 = hand.landmarks[this.handPointIndex(args.POINT2)];

        return this.stageDistance(p1, p2);
    }

    angleOfHandFinger (args) {
        const hand = this.getHand(args.HAND);
        if (!hand) return 0;

        const points = this.handFingerPoints(args.FINGER);
        if (!points) return 0;

        const a = hand.landmarks[points[0]];
        const b = hand.landmarks[points[1]];
        const c = hand.landmarks[points[2]];

        return this.angleBetween(a, b, c);
    }

    /* --------------------------------------------------
     * Body + hand combined
     * -------------------------------------------------- */

    isHandNearBodyArea (args) {
        const hands = this.getHandsForSelection(args.HAND);
        const area = this.getBodyAreaPoint(args.AREA);
        if (!area || hands.length < 1) return false;

        return hands.some(hand => {
            const wrist = hand.landmarks[0];
            return this.normalizedDistance(wrist, area) < 0.18;
        });
    }

    isHandAboveBodyArea (args) {
        const hands = this.getHandsForSelection(args.HAND);
        const area = this.getBodyAreaPoint(args.AREA);
        if (!area || hands.length < 1) return false;

        return hands.some(hand => hand.landmarks[0].y < area.y);
    }

    areBothHandsState (args) {
        const left = this.getHand('left hand');
        const right = this.getHand('right hand');
        if (!left || !right) return false;

        const lw = left.landmarks[0];
        const rw = right.landmarks[0];

        switch (args.STATE) {
        case 'joined':
            return this.normalizedDistance(lw, rw) < 0.12;
        case 'apart':
            return this.normalizedDistance(lw, rw) > 0.35;
        case 'crossed':
            return this.stageX(lw.x) > this.stageX(rw.x);
        case 'raised':
            return this.detectPose('both hands up');
        case 'down':
            return this.detectPose('left hand down') && this.detectPose('right hand down');
        default:
            return false;
        }
    }

    /* --------------------------------------------------
     * Events
     * -------------------------------------------------- */

    whenBodyDetected () {
        return this.isBodyDetected();
    }

    whenBodyLost () {
        return this.running && !this.isBodyDetected();
    }

    whenHandDetected (args) {
        return this.isHandTypeDetected({HAND: args.HAND});
    }

    whenHandLost (args) {
        return this.running && !this.isHandTypeDetected({HAND: args.HAND});
    }

    whenPoseDetected (args) {
        return this.detectPose(args.POSE);
    }

    whenHandGestureDetected (args) {
        return this.isHandGestureDetected({GESTURE: args.GESTURE});
    }

    whenHandGestureOnHandDetected (args) {
        return this.isHandGestureOnHandDetected({
            HAND: args.HAND,
            GESTURE: args.GESTURE
        });
    }

    whenBodyMoves (args) {
        return this.bodyMovementDirectionValue === args.DIRECTION;
    }

    whenNumberOfFingersIs (args) {
        return this.numberOfFingersUp({HAND: args.HAND}) === Cast.toNumber(args.NUM);
    }

    /* --------------------------------------------------
     * Overlay blocks
     * -------------------------------------------------- */

    setBodyOverlay (args) {
        this.overlay.bodyPoints = args.POINTS === 'show';
        this.overlay.bodyLines = args.LINES === 'show';
    }

    setHandOverlay (args) {
        this.overlay.handPoints = args.POINTS === 'show';
        this.overlay.handLines = args.LINES === 'show';
    }

    setMinimumConfidence (args) {
        this.minimumConfidence = this.clamp(Cast.toNumber(args.VALUE), 1, 100);
    }

    clearPoseOverlay () {
        if (this.overlayCtx && this.overlayCanvas) {
            this.overlayCtx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
        }
    }

    setBodyPointColor (args) {
        this.colors.bodyPoint = String(args.COLOR || '#ff0000');
    }

    setBodyLineColor (args) {
        this.colors.bodyLine = String(args.COLOR || '#00ff00');
    }

    setHandPointColor (args) {
        this.colors.handPoint = String(args.COLOR || '#ff0000');
    }

    setHandLineColor (args) {
        this.colors.handLine = String(args.COLOR || '#00ff00');
    }

    createOverlay () {
        if (this.overlayContainer && this.overlayCanvas) return;

        this.stageHost = this.findStageHost();
        const mountNode = this.stageHost || document.body;

        if (mountNode !== document.body) {
            const computed = window.getComputedStyle(mountNode);
            if (computed.position === 'static') {
                mountNode.style.position = 'relative';
            }
        }

        this.overlayContainer = document.createElement('div');
        this.overlayContainer.id = 'madbot-pose-ai-overlay';

        Object.assign(this.overlayContainer.style, {
            position: mountNode === document.body ? 'fixed' : 'absolute',
            left: '0',
            top: '0',
            width: mountNode === document.body ? '320px' : '100%',
            height: mountNode === document.body ? '240px' : '100%',
            background: 'transparent',
            overflow: 'hidden',
            zIndex: '60',
            display: this.showPreview ? 'block' : 'none',
            pointerEvents: 'none'
        });

        this.overlayCanvas = document.createElement('canvas');
        this.overlayCanvas.width = 480;
        this.overlayCanvas.height = 360;

        Object.assign(this.overlayCanvas.style, {
            position: 'absolute',
            left: '0',
            top: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
        });

        this.overlayCtx = this.overlayCanvas.getContext('2d');
        this.overlayContainer.appendChild(this.overlayCanvas);
        mountNode.appendChild(this.overlayContainer);
    }

    findStageHost () {
        const candidates = Array.from(document.querySelectorAll('div'))
            .filter(el => {
                const rect = el.getBoundingClientRect();
                const hasCanvas = !!el.querySelector('canvas');
                const goodSize = rect.width > 250 && rect.height > 180;
                const ratio = rect.width / Math.max(1, rect.height);
                const stageRatio = ratio > 1.1 && ratio < 1.7;
                return hasCanvas && goodSize && stageRatio;
            });

        if (candidates.length > 0) {
            return candidates[candidates.length - 1];
        }

        return (
            document.querySelector('[class*="stage-wrapper"]') ||
            document.querySelector('[class*="stage_stage"]') ||
            document.body
        );
    }

    drawOverlay () {
        if (!this.overlayCtx || !this.overlayCanvas) return;

        const ctx = this.overlayCtx;
        ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);

        if (this.body) {
            this.drawBodyOverlay(ctx, this.body);
        }

        this.hands.forEach(hand => {
            this.drawHandOverlay(ctx, hand);
        });
    }

    drawBodyOverlay (ctx, body) {
        const lines = [
            ['left shoulder', 'right shoulder'],
            ['left shoulder', 'left elbow'],
            ['left elbow', 'left wrist'],
            ['right shoulder', 'right elbow'],
            ['right elbow', 'right wrist'],
            ['left shoulder', 'left hip'],
            ['right shoulder', 'right hip'],
            ['left hip', 'right hip'],
            ['left hip', 'left knee'],
            ['left knee', 'left ankle'],
            ['right hip', 'right knee'],
            ['right knee', 'right ankle']
        ];

        if (this.overlay.bodyLines) {
            ctx.strokeStyle = this.colors.bodyLine;
            ctx.lineWidth = 3;
            lines.forEach(pair => {
                const p1 = body.landmarks[this.bodyPointIndex(pair[0])];
                const p2 = body.landmarks[this.bodyPointIndex(pair[1])];
                this.drawLine(ctx, p1, p2);
            });
        }

        if (this.overlay.bodyPoints || this.overlay.bodyLabels) {
            Object.keys(this.bodyPointMap()).forEach(name => {
                const p = body.landmarks[this.bodyPointIndex(name)];
                if (!p) return;

                if (this.overlay.bodyPoints) {
                    this.drawPoint(ctx, p, this.colors.bodyPoint, 4);
                }

                if (this.overlay.bodyLabels) {
                    this.drawLabel(ctx, p, name);
                }
            });
        }
    }

    drawHandOverlay (ctx, hand) {
        const lines = [
            [0, 1], [1, 2], [2, 3], [3, 4],
            [0, 5], [5, 6], [6, 7], [7, 8],
            [0, 9], [9, 10], [10, 11], [11, 12],
            [0, 13], [13, 14], [14, 15], [15, 16],
            [0, 17], [17, 18], [18, 19], [19, 20]
        ];

        if (this.overlay.handLines) {
            ctx.strokeStyle = this.colors.handLine;
            ctx.lineWidth = 3;
            lines.forEach(pair => {
                this.drawLine(ctx, hand.landmarks[pair[0]], hand.landmarks[pair[1]]);
            });
        }

        if (this.overlay.handPoints || this.overlay.handLabels) {
            hand.landmarks.forEach((p, index) => {
                if (this.overlay.handPoints) {
                    this.drawPoint(ctx, p, this.colors.handPoint, 4);
                }

                if (this.overlay.handLabels) {
                    this.drawLabel(ctx, p, String(index));
                }
            });
        }
    }

    drawLine (ctx, p1, p2) {
        if (!p1 || !p2) return;

        ctx.beginPath();
        ctx.moveTo(this.canvasX(p1.x), this.canvasY(p1.y));
        ctx.lineTo(this.canvasX(p2.x), this.canvasY(p2.y));
        ctx.stroke();
    }

    drawPoint (ctx, p, color, radius) {
        if (!p) return;

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(this.canvasX(p.x), this.canvasY(p.y), radius, 0, Math.PI * 2);
        ctx.fill();
    }

    drawLabel (ctx, p, text) {
        if (!p) return;

        const x = this.canvasX(p.x);
        const y = this.canvasY(p.y);

        ctx.font = '12px Arial';
        const width = ctx.measureText(text).width + 8;

        ctx.fillStyle = this.colors.labelBg;
        ctx.fillRect(x + 5, y - 16, width, 16);

        ctx.fillStyle = this.colors.labelText;
        ctx.fillText(text, x + 9, y - 4);
    }

    /* --------------------------------------------------
     * Helper functions
     * -------------------------------------------------- */

    bodyPointMap () {
        return {
            'nose': 0,
            'left eye': 2,
            'right eye': 5,
            'left ear': 7,
            'right ear': 8,
            'left shoulder': 11,
            'right shoulder': 12,
            'left elbow': 13,
            'right elbow': 14,
            'left wrist': 15,
            'right wrist': 16,
            'left hip': 23,
            'right hip': 24,
            'left knee': 25,
            'right knee': 26,
            'left ankle': 27,
            'right ankle': 28,
            'left heel': 29,
            'right heel': 30,
            'left foot': 31,
            'right foot': 32
        };
    }

    bodyPointIndex (name) {
        const map = this.bodyPointMap();
        return Object.prototype.hasOwnProperty.call(map, name) ? map[name] : 0;
    }

    handPointMap () {
        return {
            'wrist': 0,
            'thumb base': 1,
            'thumb lower joint': 2,
            'thumb upper joint': 3,
            'thumb tip': 4,
            'index finger base': 5,
            'index finger lower joint': 6,
            'index finger middle joint': 7,
            'index finger tip': 8,
            'middle finger base': 9,
            'middle finger lower joint': 10,
            'middle finger middle joint': 11,
            'middle finger tip': 12,
            'ring finger base': 13,
            'ring finger lower joint': 14,
            'ring finger middle joint': 15,
            'ring finger tip': 16,
            'pinky finger base': 17,
            'pinky finger lower joint': 18,
            'pinky finger middle joint': 19,
            'pinky finger tip': 20
        };
    }

    handPointIndex (name) {
        const map = this.handPointMap();
        return Object.prototype.hasOwnProperty.call(map, name) ? map[name] : 0;
    }

    bodyJointPoints (joint) {
        const map = {
            'left elbow': ['left shoulder', 'left elbow', 'left wrist'],
            'right elbow': ['right shoulder', 'right elbow', 'right wrist'],
            'left knee': ['left hip', 'left knee', 'left ankle'],
            'right knee': ['right hip', 'right knee', 'right ankle'],
            'left shoulder': ['left elbow', 'left shoulder', 'left hip'],
            'right shoulder': ['right elbow', 'right shoulder', 'right hip'],
            'left hip': ['left shoulder', 'left hip', 'left knee'],
            'right hip': ['right shoulder', 'right hip', 'right knee']
        };

        return map[joint] || null;
    }

    handFingerPoints (finger) {
        const map = {
            'thumb': [2, 3, 4],
            'index finger': [5, 6, 8],
            'middle finger': [9, 10, 12],
            'ring finger': [13, 14, 16],
            'pinky finger': [17, 18, 20]
        };

        return map[finger] || null;
    }

    getBodyLandmark (pointName) {
        if (!this.body) return null;
        return this.body.landmarks[this.bodyPointIndex(pointName)] || null;
    }

    getBodyAreaPoint (area) {
        const p = name => this.getBodyLandmark(name);

        const nose = p('nose');
        const leftShoulder = p('left shoulder');
        const rightShoulder = p('right shoulder');
        const leftHip = p('left hip');
        const rightHip = p('right hip');

        switch (area) {
        case 'face':
        case 'head':
            return nose;
        case 'chest':
            return this.averageLandmarks(leftShoulder, rightShoulder);
        case 'left shoulder':
            return leftShoulder;
        case 'right shoulder':
            return rightShoulder;
        case 'waist':
            return this.averageLandmarks(leftHip, rightHip);
        default:
            return null;
        }
    }

    averageLandmarks (a, b) {
        if (!a || !b) return null;
        return {
            x: (a.x + b.x) / 2,
            y: (a.y + b.y) / 2,
            z: ((a.z || 0) + (b.z || 0)) / 2,
            visibility: ((a.visibility || 0) + (b.visibility || 0)) / 2
        };
    }

    getHand (selection) {
        if (!selection || selection === 'any hand') return this.hands[0] || null;

        if (selection === 'left hand') {
            return this.hands.find(hand => hand.side === 'left') || null;
        }

        if (selection === 'right hand') {
            return this.hands.find(hand => hand.side === 'right') || null;
        }

        if (selection.indexOf('hand number') === 0) {
            const number = Cast.toNumber(selection.replace('hand number', '').trim());
            return this.hands[Math.max(0, number - 1)] || null;
        }

        return null;
    }

    getHandsForSelection (selection) {
        if (selection === 'any hand') return this.hands;
        const hand = this.getHand(selection);
        return hand ? [hand] : [];
    }

    fingersState (hand) {
        return {
            thumb: this.fingerIsUp(hand, 'thumb'),
            'index finger': this.fingerIsUp(hand, 'index finger'),
            'middle finger': this.fingerIsUp(hand, 'middle finger'),
            'ring finger': this.fingerIsUp(hand, 'ring finger'),
            'pinky finger': this.fingerIsUp(hand, 'pinky finger')
        };
    }

    fingerIsUp (hand, finger) {
        if (!hand || !hand.landmarks) return false;

        const lm = hand.landmarks;

        if (finger === 'thumb') {
            const tip = lm[4];
            const base = lm[2];
            const wrist = lm[0];
            return this.normalizedDistance(tip, wrist) > this.normalizedDistance(base, wrist) * 1.15;
        }

        const map = {
            'index finger': [8, 6],
            'middle finger': [12, 10],
            'ring finger': [16, 14],
            'pinky finger': [20, 18]
        };

        const pair = map[finger];
        if (!pair) return false;

        const tip = lm[pair[0]];
        const joint = lm[pair[1]];

        return tip && joint && tip.y < joint.y;
    }

    countFingersUp (hand) {
        const fingers = this.fingersState(hand);
        return Object.keys(fingers).filter(key => fingers[key]).length;
    }

    landmarkValue (landmark, position) {
        if (!landmark) return 0;

        switch (position) {
        case 'x position':
            return this.stageX(landmark.x);
        case 'y position':
            return this.stageY(landmark.y);
        case 'z position':
            return Math.round((landmark.z || 0) * 100);
        case 'visibility':
            return Math.round((landmark.visibility || 1) * 100);
        default:
            return 0;
        }
    }

    getBoundsFromLandmarks (landmarks) {
        const visible = landmarks.filter(p => p && (p.visibility === undefined || p.visibility > 0.3));

        if (visible.length < 1) {
            return {
                centerX: 0,
                centerY: 0,
                width: 0,
                height: 0,
                centerXStage: 0,
                centerYStage: 0,
                widthStage: 0,
                heightStage: 0,
                topYStage: 0,
                bottomYStage: 0
            };
        }

        const xs = visible.map(p => p.x);
        const ys = visible.map(p => p.y);

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        return {
            centerX,
            centerY,
            width: maxX - minX,
            height: maxY - minY,
            centerXStage: this.stageX(centerX),
            centerYStage: this.stageY(centerY),
            widthStage: Math.round((maxX - minX) * 480),
            heightStage: Math.round((maxY - minY) * 360),
            topYStage: this.stageY(minY),
            bottomYStage: this.stageY(maxY)
        };
    }

    averageVisibility (landmarks) {
        if (!landmarks || landmarks.length < 1) return 0;

        const values = landmarks
            .map(p => p.visibility === undefined ? 1 : p.visibility)
            .filter(v => typeof v === 'number');

        if (values.length < 1) return 0;

        const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
        return Math.round(avg * 100);
    }

    stageX (normalizedX) {
        const x = this.cameraSource === 'front' ? 1 - normalizedX : normalizedX;
        return Math.round((x * 480) - 240);
    }

    stageY (normalizedY) {
        return Math.round(180 - (normalizedY * 360));
    }

    canvasX (normalizedX) {
        const x = this.cameraSource === 'front' ? 1 - normalizedX : normalizedX;
        return x * 480;
    }

    canvasY (normalizedY) {
        return normalizedY * 360;
    }

    normalizedDistance (p1, p2) {
        if (!p1 || !p2) return 0;

        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        return Math.sqrt((dx * dx) + (dy * dy));
    }

    stageDistance (p1, p2) {
        if (!p1 || !p2) return 0;

        const dx = this.stageX(p1.x) - this.stageX(p2.x);
        const dy = this.stageY(p1.y) - this.stageY(p2.y);

        return Math.round(Math.sqrt((dx * dx) + (dy * dy)));
    }

    angleBetween (a, b, c) {
        if (!a || !b || !c) return 0;

        const ab = {
            x: a.x - b.x,
            y: a.y - b.y
        };

        const cb = {
            x: c.x - b.x,
            y: c.y - b.y
        };

        const dot = (ab.x * cb.x) + (ab.y * cb.y);
        const magAB = Math.sqrt((ab.x * ab.x) + (ab.y * ab.y));
        const magCB = Math.sqrt((cb.x * cb.x) + (cb.y * cb.y));

        if (!magAB || !magCB) return 0;

        const cosine = this.clamp(dot / (magAB * magCB), -1, 1);
        return Math.round(Math.acos(cosine) * 180 / Math.PI);
    }

    clamp (value, min, max) {
        return Math.max(min, Math.min(max, value));
    }

    showLoadingPopup (message) {
        let popup = document.getElementById('madbot-pose-ai-loader');

        if (!popup) {
            popup = document.createElement('div');
            popup.id = 'madbot-pose-ai-loader';

            Object.assign(popup.style, {
                position: 'fixed',
                inset: '0',
                background: 'rgba(0, 0, 0, 0.55)',
                zIndex: 999999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial, sans-serif'
            });

            popup.innerHTML = `
                <div style="
                    width: 380px;
                    background: #ffffff;
                    border-radius: 18px;
                    padding: 28px;
                    text-align: center;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.35);
                    border-top: 8px solid #6A20B0;
                ">
                    <div style="
                        width: 54px;
                        height: 54px;
                        margin: 0 auto 18px;
                        border: 6px solid #e6d8f6;
                        border-top: 6px solid #6A20B0;
                        border-radius: 50%;
                        animation: madbotPoseSpin 0.9s linear infinite;
                    "></div>
                    <div style="font-size: 22px; font-weight: bold; color: #6A20B0;">
                        Loading MadBot Pose AI
                    </div>
                    <div id="madbot-pose-ai-loader-message" style="
                        margin-top: 12px;
                        color: #575E75;
                        font-size: 15px;
                        line-height: 1.4;
                    ">
                        Please wait...
                    </div>
                </div>
            `;

            const style = document.createElement('style');
            style.textContent = `
                @keyframes madbotPoseSpin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
            document.body.appendChild(popup);
        }

        const messageNode = document.getElementById('madbot-pose-ai-loader-message');
        if (messageNode) messageNode.textContent = message;
    }

    hideLoadingPopup () {
        const popup = document.getElementById('madbot-pose-ai-loader');
        if (popup) popup.remove();
    }

    showLoadingError (message) {
        let popup = document.getElementById('madbot-pose-ai-loader');

        if (!popup) {
            this.showLoadingPopup('Error');
            popup = document.getElementById('madbot-pose-ai-loader');
        }

        if (!popup) return;

        popup.innerHTML = `
            <div style="
                width: 430px;
                background: #ffffff;
                border-radius: 18px;
                padding: 28px;
                text-align: center;
                box-shadow: 0 12px 40px rgba(0,0,0,0.35);
                border-top: 8px solid #ff3131;
                font-family: Arial, sans-serif;
            ">
                <div style="font-size: 22px; font-weight: bold; color: #ff3131;">
                    MadBot Pose AI Error
                </div>
                <div style="
                    margin-top: 12px;
                    color: #575E75;
                    font-size: 14px;
                    line-height: 1.4;
                    word-break: break-word;
                ">
                    ${message}
                </div>
                <button id="madbot-pose-ai-close-error" style="
                    margin-top: 20px;
                    background: #6A20B0;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 18px;
                    font-weight: bold;
                    cursor: pointer;
                ">
                    Close
                </button>
            </div>
        `;

        const closeButton = document.getElementById('madbot-pose-ai-close-error');
        if (closeButton) closeButton.onclick = () => this.hideLoadingPopup();
    }
}

module.exports = Scratch3MadBotPoseAI;
    